import { saveAs } from 'file-saver';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { BulkImportUserType } from '~/enums/BulkImportUserType';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import { ImportUsersSummaryType } from '~/redux/api/methods/users';
import { Uuid } from '~/types/Uuid';

import BulkImportUserWizard from '../UsersPage/BulkUserImportWizard/BulkImportUserWizard';

type Props = {
  clientId: number;
  onClose: () => void;
  onImport: (importId: Uuid) => void;
};

export const ImportUsersWizard = ({ clientId, onClose, onImport }: Props) => {
  const { toast } = useToast();
  const intl = useIntl();
  const formatDate = useFormatDate();

  const [importSummaryData, setImportSummaryData] = useState<
    ImportUsersSummaryType | undefined
  >();
  const [importStep, setImportStep] = useState<BulkImportUserType>(
    BulkImportUserType.Upload,
  );
  const [importStepLoading, setImportStepLoading] = useState(false);
  const [exportUsersExcelFile] =
    api.endpoints.exportUsersExcelFile.useLazyQuery();
  const [downloadImportResultsExcelFile] =
    api.endpoints.downloadImportResultsExcelFile.useLazyQuery();

  const [startImportUsersListProcessing] =
    api.endpoints.startImportUsersListProcessing.useMutation();

  const [importUsersListExcelFile] =
    api.endpoints.importUsersListExcelFile.useMutation();

  const errorDownloadFileMessage = intl.formatMessage({
    id: 'ImportUsersWizard.errorDownloadingFile',
    defaultMessage: 'Error downloading file.',
    description: 'Message shown when there is an error downloading file',
  });

  const handleUserDownloadClick = async (onlyHeaders: any) => {
    let filename = 'evisort-users-import-template.xlsx';
    if (!onlyHeaders) {
      const timestamp = formatDate(new Date(), 'utc');
      filename = `evisort-users-${timestamp}.xlsx`;
    }

    try {
      const res = await exportUsersExcelFile({
        clientId,
        onlyHeaders,
      }).unwrap();
      toast({
        status: 'success',
        message: intl.formatMessage({
          id: 'ImportUsersWizard.fileDownloaded',
          defaultMessage: 'File downloaded successfully.',
          description:
            'Message shown when file is downloaded successfully. Example: resultType = "document"',
        }),
      });
      saveAs(res as unknown as Blob, filename);
    } catch {
      toast({
        status: 'danger',
        message: errorDownloadFileMessage,
      });
    }
  };

  const handleImportResultsFileDownloadClick = async (resultType: any) => {
    const importId = importSummaryData?.import_id;
    if (!importId) {
      return;
    }
    try {
      const res = await downloadImportResultsExcelFile({
        clientId,
        importId,
        resultType,
      }).unwrap();
      toast({
        status: 'success',
        message: intl.formatMessage(
          {
            id: 'ImportUsersWizard.resultTypeFileDownloaded',
            defaultMessage: `{resultType} file downloaded successfully.`,
            description:
              'Message shown when file is downloaded successfully. Example: resultType = "document"',
          },
          {
            resultType,
          },
        ),
      });
      const timestamp = formatDate(new Date(), 'utc');
      const filename = `evisort-users-${resultType}-${timestamp}.xlsx`;
      saveAs(res as unknown as Blob, filename);
    } catch {
      toast({
        status: 'danger',
        message: errorDownloadFileMessage,
      });
    }
  };

  const handleSelectedImportStep = (importStep: any) => {
    setImportStep(importStep);
  };

  const handleHideImportWizard = () => {
    onClose();
    setImportStep(BulkImportUserType.Upload);
    setImportStepLoading(false);
  };

  const handleUserListUploadClick = async (file: Blob) => {
    setImportStepLoading(true);

    try {
      const response = await importUsersListExcelFile({
        file,
        clientId,
      }).unwrap();
      toast({
        status: 'success',
        message: intl.formatMessage({
          id: 'ImportUsersWizard.successfulImport',
          defaultMessage: 'Users List file has been imported successfully.',
          description:
            'Message shown when users list file is imported successfully',
        }),
      });
      setImportSummaryData(response);
      setImportStep(BulkImportUserType.Summary);
      setImportStepLoading(false);
    } catch {
      toast({
        status: 'danger',
        message: intl.formatMessage({
          id: 'ImportUsersWizard.errorImportingFile',
          defaultMessage: 'Error importing file.',
          description: 'Message shown when there is an error importing file',
        }),
      });
      setImportStep(BulkImportUserType.Upload);
      setImportStepLoading(false);
    }
  };
  const handleStartImportUserBulkProcessing = async (
    importActions: any,
    hideModalFn: () => void,
  ) => {
    setImportStepLoading(true);
    try {
      if (importSummaryData) {
        await startImportUsersListProcessing({
          client_id: clientId,
          import_id: importSummaryData?.import_id ?? '',
          email_new_users: importActions.emailNewUsers,
          update_existing_users: importActions.updateExistingUsers,
          delete_missing_users: importActions.deleteMissingUsers,
        }).unwrap();

        onImport(importSummaryData.import_id);
        toast({
          status: 'success',
          message: intl.formatMessage({
            id: 'ImportUsersWizard.requestQueued',
            defaultMessage:
              'Request to start Import Users List processing successfully queued.',
            description: 'Message shown when import request is queued',
          }),
        });
        handleHideImportWizard();
        hideModalFn();
      }
    } catch {
      toast({
        status: 'danger',
        message: intl.formatMessage({
          id: 'ImportUsersWizard.errorStartingImport',
          defaultMessage: 'Error starting import processing.',
          description:
            'Message shown when there is an error starting import processing',
        }),
      });
      setImportStepLoading(false);
    }
  };

  return (
    <BulkImportUserWizard
      handleDownloadUserTemplateFile={handleUserDownloadClick}
      handleImportResultsFileDownload={handleImportResultsFileDownloadClick}
      handleSelectedStep={handleSelectedImportStep}
      onClose={handleHideImportWizard}
      importSummaryData={importSummaryData}
      onUploadActionClick={handleUserListUploadClick}
      step={importStep}
      stepLoading={importStepLoading}
      onStartImportUserBulkProcessing={handleStartImportUserBulkProcessing}
    />
  );
};
